import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import AnimateOnScroll, { StaggerContainer, StaggerItem } from '@/components/common/AnimateOnScroll';
import { TeacherGridCardSkeleton, CourseGridCardSkeleton } from '@/components/common/LoadingSkeleton';
import { SEO, createBreadcrumbSchema, createFAQSchema } from '@/components/seo';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useTeachers } from '@/hooks/use-public-api';
import { useCourses } from '@/hooks/use-course-api';
import { specialtyLabel } from '@/lib/specialties';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { LANDINGS, type LandingCopy } from '@/content/landings';
import {
  ArrowRight,
  Star,
  BadgeCheck,
  BookOpen,
  Sparkles,
  Ear,
  Route,
  CalendarClock,
  LineChart,
  Award,
  UserPlus,
  Search,
  CalendarCheck,
  Video,
} from 'lucide-react';

/**
 * One component, six SEO landing pages: /tajweed-classes, /hifz-program,
 * /ijazah, /quranic-arabic, /quran-for-adults, /female-quran-teachers.
 *
 * Structure mirrors /quran-for-kids (hero, live stats, why, tracks, real
 * teacher grid filtered to the specialty, courses, how it works, FAQ with
 * FAQPage schema, CTA). Copy lives in src/content/landings.ts in both
 * languages; counts and ratings come from the listing so nothing on the
 * page can drift from the platform.
 */

const HONORIFICS = new Set([
  'ustadh', 'ustadha', 'sheikh', 'shaykh', 'sheikha', 'shaykha', 'qari', 'qariah', 'hafiz', 'hafiza', 'dr', 'dr.', 'imam',
  'الأستاذ', 'الأستاذة', 'أستاذ', 'أستاذة', 'الشيخ', 'الشيخة', 'شيخ', 'شيخة', 'القارئ', 'القارئة', 'الحافظ', 'الحافظة', 'د.', 'الدكتور', 'الدكتورة',
]);

function initialsOf(name: string): string {
  const words = name.split(/\s+/).filter(Boolean).filter((w) => !HONORIFICS.has(w.toLowerCase()));
  return (words.length ? words : name.split(/\s+/).filter(Boolean))
    .slice(0, 2)
    .map((w) => Array.from(w)[0] ?? '')
    .join('');
}

const TEACHER_FETCH_LIMIT = 24;
const TEACHER_SHOWN = 6;
const WHY_ICONS = [Ear, Route, BookOpen, CalendarClock, LineChart, Award] as const;
const HOW_ICONS = [UserPlus, Search, CalendarCheck, Video] as const;

interface LandingTeacher {
  name: string;
  slug?: string;
  full_name: string;
  full_name_ar?: string;
  profile_image?: string;
  specialties?: string;
  specialties_list?: string[];
  hourly_rate: number;
  trial_session_price?: number;
  ijazah_verified?: number;
  average_rating?: number;
}

interface LandingCourse {
  name: string;
  slug: string;
  title: string;
  title_ar?: string;
  thumbnail?: string;
  total_lessons?: number;
  is_free?: boolean;
  effective_price?: number;
  price?: number;
}

const TeacherCard = ({ teacher }: { teacher: LandingTeacher }) => {
  const { t, isRTL } = useLanguage();
  const { formatPrice } = useCurrency();
  const name = isRTL && teacher.full_name_ar ? teacher.full_name_ar : teacher.full_name;
  const specialties =
    teacher.specialties_list ||
    (teacher.specialties ? teacher.specialties.split(',').map((s) => s.trim()).filter(Boolean) : []);
  const trial = teacher.trial_session_price;

  return (
    <Link
      to={`/teachers/${encodeURIComponent(teacher.slug || teacher.name)}`}
      className="h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-xl"
    >
      <motion.div
        whileHover={{ y: -4 }}
        transition={{ duration: 0.2 }}
        className="bg-card rounded-xl border border-border overflow-hidden hover:shadow-soft-lg hover:border-primary/20 transition-all duration-300 h-full flex flex-col"
      >
        <div className="aspect-[4/3] bg-muted overflow-hidden">
          {teacher.profile_image ? (
            <img src={teacher.profile_image} alt={name} loading="lazy" decoding="async" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-emerald-600 to-emerald-800" aria-hidden="true">
              <span className="text-3xl font-semibold text-amber-300/90 tracking-wide">{initialsOf(name)}</span>
            </div>
          )}
        </div>
        <div className="p-5 flex flex-col flex-1">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="font-semibold text-foreground leading-tight">{name}</h3>
            {teacher.average_rating ? (
              <span className="flex items-center gap-1 text-sm text-muted-foreground shrink-0">
                <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" aria-hidden="true" />
                {teacher.average_rating.toFixed(2)}
              </span>
            ) : null}
          </div>
          {teacher.ijazah_verified ? (
            <p className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400 mb-3">
              <BadgeCheck className="h-3.5 w-3.5" aria-hidden="true" />
              {t('kids.teachers.ijazahVerified')}
            </p>
          ) : (
            <div className="mb-3" />
          )}
          {specialties.length > 0 && (
            <ul className="flex flex-wrap gap-1.5 mb-4">
              {specialties.slice(0, 3).map((s) => (
                <li key={s} className="text-xs bg-muted text-muted-foreground rounded-full px-2.5 py-1">
                  {specialtyLabel(s, isRTL)}
                </li>
              ))}
            </ul>
          )}
          <div className="mt-auto pt-3 border-t border-border flex items-baseline justify-between gap-2">
            <span className="text-foreground font-semibold">
              {formatPrice(teacher.hourly_rate)}
              <span className="text-xs text-muted-foreground font-normal">{t('kids.teachers.perHour')}</span>
            </span>
            {trial === 0 ? (
              <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">{t('kids.teachers.trialFree')}</span>
            ) : trial ? (
              <span className="text-xs text-muted-foreground">
                {t('kids.teachers.trialFrom')} {formatPrice(trial)}
              </span>
            ) : null}
          </div>
        </div>
      </motion.div>
    </Link>
  );
};

const CourseCard = ({ course }: { course: LandingCourse }) => {
  const { t, isRTL } = useLanguage();
  const { formatPrice } = useCurrency();
  const title = isRTL && course.title_ar ? course.title_ar : course.title;
  const price = course.effective_price ?? course.price ?? 0;
  return (
    <Link
      to={`/courses/${encodeURIComponent(course.slug || course.name)}`}
      className="h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-xl"
    >
      <motion.div whileHover={{ y: -4 }} transition={{ duration: 0.2 }} className="bg-card rounded-xl border border-border overflow-hidden hover:shadow-soft-lg hover:border-primary/20 transition-all duration-300 h-full flex flex-col">
        <div className="aspect-video bg-muted overflow-hidden">
          {course.thumbnail ? (
            <img src={course.thumbnail} alt={title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-emerald-700 to-emerald-900" aria-hidden="true">
              <BookOpen className="h-9 w-9 text-amber-300/80" />
            </div>
          )}
        </div>
        <div className="p-5 flex flex-col flex-1">
          <h3 className="font-semibold text-foreground leading-snug mb-2">{title}</h3>
          {course.total_lessons ? (
            <p className="text-sm text-muted-foreground">{course.total_lessons} {t('kids.courses.lessons')}</p>
          ) : null}
          <span className="mt-auto pt-3 text-foreground font-semibold">{course.is_free ? t('kids.courses.free') : formatPrice(price)}</span>
        </div>
      </motion.div>
    </Link>
  );
};

const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <p className="text-sm font-semibold text-amber-500 uppercase tracking-wider mb-2">{children}</p>
);

const SpecialtyLanding = ({ slug }: { slug: string }) => {
  const config = LANDINGS[slug];
  const { t, isRTL, language } = useLanguage();
  const prefersReducedMotion = useReducedMotion();
  const copy: LandingCopy = isRTL ? config.ar : config.en;
  const url = `https://hudanlilmuttaqeen.com${config.path}`;

  const { data: teacherData, isLoading: teachersLoading, isError: teachersError } = useTeachers({
    ...config.teacherFilter,
    limit: TEACHER_FETCH_LIMIT,
    sort_by: 'average_rating',
    sort_order: 'desc',
  });
  // CourseFilters.category resolves to a duplicated union in types/api.ts (pre-existing); the endpoint takes plain strings.
  const { data: courseData, isLoading: coursesLoading } = useCourses({ ...config.courseFilter, limit: 3 } as Parameters<typeof useCourses>[0]);

  const teachers = (teacherData?.teachers as unknown as LandingTeacher[] | undefined) ?? [];
  const courses = (courseData?.courses as unknown as LandingCourse[] | undefined) ?? [];
  const teacherCount = teacherData?.total ?? teachers.length;
  const averageRating = useMemo(() => {
    const rated = teachers.filter((x) => (x.average_rating ?? 0) > 0);
    if (!rated.length) return null;
    return (rated.reduce((sum, x) => sum + (x.average_rating ?? 0), 0) / rated.length).toFixed(2);
  }, [teachers]);

  const structuredData = useMemo(() => {
    const inLanguage = isRTL ? 'ar' : 'en';
    return {
      '@context': 'https://schema.org',
      '@graph': [
        createFAQSchema(copy.faq.items.map((f) => ({ question: f.q, answer: f.a })), inLanguage),
        createBreadcrumbSchema(
          [
            { name: t('nav.home'), url: 'https://hudanlilmuttaqeen.com/' },
            { name: copy.breadcrumb, url },
          ],
          inLanguage
        ),
      ],
    };
    // language flips t() and copy; isRTL is derived from it.
  }, [copy, t, isRTL, language, url]);

  const dir = isRTL ? 'rtl' : 'ltr';

  return (
    <Layout>
      <SEO title={copy.seo.title} description={copy.seo.description} keywords={copy.seo.keywords} url={url} structuredData={structuredData} />

      {/* Hero */}
      <section className="relative overflow-hidden bg-hero-gradient dark:bg-hero-gradient-dark" dir={dir}>
        <div className="absolute inset-0 islamic-pattern-overlay opacity-60" />
        <div className="absolute top-10 end-[10%] w-[500px] h-[500px] rounded-full bg-amber-400/8 blur-[100px] pointer-events-none" />
        <div className="container relative z-10 py-16 md:py-24">
          <motion.div
            initial={prefersReducedMotion ? undefined : { opacity: 0, y: 24 }}
            animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full px-4 py-1.5 mb-6">
              <Sparkles className="h-3.5 w-3.5 text-amber-400" aria-hidden="true" />
              <span className="text-emerald-100 text-sm font-medium">{copy.hero.badge}</span>
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 leading-[1.15]">{copy.hero.title}</h1>
            <p className="text-lg md:text-xl text-emerald-100/80 mb-8 leading-relaxed">{copy.hero.subtitle}</p>
            <div className="flex flex-col sm:flex-row gap-3 mb-10">
              <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-500/90 text-amber-950 font-semibold rounded-xl px-7 h-12 text-base shadow-glow-gold">
                <Link to={config.primaryHref} className="flex items-center gap-2">
                  {copy.hero.ctaPrimary}
                  <ArrowRight className="h-4 w-4 rtl-mirror" aria-hidden="true" />
                </Link>
              </Button>
              <Button asChild size="lg" className="bg-transparent border border-white/30 text-white hover:bg-white/10 rounded-xl px-7 h-12 text-base">
                <Link to="/teachers">{copy.hero.ctaSecondary}</Link>
              </Button>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {copy.hero.trust.map((item) => (
                <li key={item} className="flex items-center gap-2 text-emerald-100/90 text-sm">
                  <BadgeCheck className="h-4 w-4 text-amber-400 shrink-0" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-b border-border bg-card" dir={dir}>
        <div className="container py-8">
          <dl className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
            <div>
              <dd className="text-2xl md:text-3xl font-bold text-foreground">{teachersLoading ? '—' : teacherCount}</dd>
              <dt className="text-xs text-muted-foreground mt-1">{copy.stats.teachersLabel}</dt>
            </div>
            <div>
              <dd className="text-2xl md:text-3xl font-bold text-foreground">{copy.stats.a.value}</dd>
              <dt className="text-xs text-muted-foreground mt-1">{copy.stats.a.label}</dt>
            </div>
            <div>
              <dd className="text-2xl md:text-3xl font-bold text-foreground">{averageRating ?? '—'}</dd>
              <dt className="text-xs text-muted-foreground mt-1">{copy.stats.ratingLabel}</dt>
            </div>
            <div>
              <dd className="text-2xl md:text-3xl font-bold text-foreground">{copy.stats.b.value}</dd>
              <dt className="text-xs text-muted-foreground mt-1">{copy.stats.b.label}</dt>
            </div>
          </dl>
        </div>
      </section>

      {/* Why */}
      <section className="section bg-background" dir={dir}>
        <div className="container">
          <AnimateOnScroll className="text-center mb-14">
            <Eyebrow>{copy.why.eyebrow}</Eyebrow>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground max-w-2xl mx-auto">{copy.why.title}</h2>
          </AnimateOnScroll>
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {copy.why.items.map((item, i) => {
              const Icon = WHY_ICONS[i % WHY_ICONS.length];
              return (
                <StaggerItem key={item.title}>
                  <div className="h-full bg-card rounded-xl border border-border p-6">
                    <span className="inline-flex items-center justify-center h-11 w-11 rounded-lg bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400 mb-4">
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <h3 className="font-semibold text-foreground mb-2">{item.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* Tracks */}
      <section className="section bg-muted/40" dir={dir}>
        <div className="container">
          <AnimateOnScroll className="text-center mb-14">
            <Eyebrow>{copy.tracks.eyebrow}</Eyebrow>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">{copy.tracks.title}</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">{copy.tracks.subtitle}</p>
          </AnimateOnScroll>
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {copy.tracks.items.map((track) => (
              <StaggerItem key={track.title}>
                <div className="h-full bg-card rounded-xl border border-border p-6 flex flex-col">
                  <span className="inline-flex self-start items-center rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 text-xs font-semibold px-3 py-1 mb-4">{track.tag}</span>
                  <h3 className="font-semibold text-foreground mb-4">{track.title}</h3>
                  <ul className="space-y-2.5">
                    {track.bullets.map((b) => (
                      <li key={b} className="flex gap-2.5 text-sm text-muted-foreground">
                        <BadgeCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" aria-hidden="true" />
                        <span className="leading-relaxed">{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Teachers */}
      <section className="section bg-background" dir={dir}>
        <div className="container">
          <AnimateOnScroll className="text-center mb-14">
            <Eyebrow>{copy.teachers.eyebrow}</Eyebrow>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">{copy.teachers.title}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">{copy.teachers.subtitle}</p>
          </AnimateOnScroll>
          {teachersLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 3 }).map((_, i) => <TeacherGridCardSkeleton key={i} />)}
            </div>
          ) : teachersError ? (
            <p className="text-center text-muted-foreground">{copy.teachers.error}</p>
          ) : teachers.length === 0 ? (
            <p className="text-center text-muted-foreground">{copy.teachers.empty}</p>
          ) : (
            <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {teachers.slice(0, TEACHER_SHOWN).map((teacher) => (
                <StaggerItem key={teacher.name}>
                  <TeacherCard teacher={teacher} />
                </StaggerItem>
              ))}
            </StaggerContainer>
          )}
          <div className="text-center mt-10">
            <Button asChild variant="outline" size="lg" className="rounded-xl">
              <Link to="/teachers" className="flex items-center gap-2">
                {copy.teachers.viewAll}
                <ArrowRight className="h-4 w-4 rtl-mirror" aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Courses */}
      {(coursesLoading || courses.length > 0) && (
        <section className="section bg-muted/40" dir={dir}>
          <div className="container">
            <AnimateOnScroll className="text-center mb-14">
              <Eyebrow>{copy.courses.eyebrow}</Eyebrow>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">{copy.courses.title}</h2>
            </AnimateOnScroll>
            {coursesLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 3 }).map((_, i) => <CourseGridCardSkeleton key={i} />)}
              </div>
            ) : (
              <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {courses.map((course) => (
                  <StaggerItem key={course.name}>
                    <CourseCard course={course} />
                  </StaggerItem>
                ))}
              </StaggerContainer>
            )}
            <div className="text-center mt-10">
              <Button asChild variant="outline" size="lg" className="rounded-xl">
                <Link to="/courses" className="flex items-center gap-2">
                  {copy.courses.viewAll}
                  <ArrowRight className="h-4 w-4 rtl-mirror" aria-hidden="true" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* How */}
      <section className="section bg-background" dir={dir}>
        <div className="container">
          <AnimateOnScroll className="text-center mb-14">
            <Eyebrow>{copy.how.eyebrow}</Eyebrow>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">{copy.how.title}</h2>
          </AnimateOnScroll>
          <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {copy.how.items.map((step, index) => {
              const Icon = HOW_ICONS[index % HOW_ICONS.length];
              return (
                <StaggerItem key={step.title}>
                  <div className="h-full bg-card rounded-xl border border-border p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="inline-flex items-center justify-center h-11 w-11 rounded-lg bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400">
                        <Icon className="h-5 w-5" aria-hidden="true" />
                      </span>
                      <span className="text-2xl font-bold text-muted-foreground/40">{String(index + 1).padStart(2, '0')}</span>
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">{step.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* FAQ */}
      <section className="section bg-muted/40" dir={dir}>
        <div className="container max-w-3xl">
          <AnimateOnScroll className="text-center mb-10">
            <Eyebrow>{copy.faq.eyebrow}</Eyebrow>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground">{copy.faq.title}</h2>
          </AnimateOnScroll>
          <Accordion type="multiple" className="space-y-2">
            {copy.faq.items.map((f, i) => (
              <AccordionItem key={f.q} value={String(i)} className="bg-card border border-border rounded-xl px-5">
                <AccordionTrigger className="text-start text-sm font-medium text-foreground hover:no-underline py-4">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground pb-4 pt-0 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-hero-gradient dark:bg-hero-gradient-dark" dir={dir}>
        <div className="absolute inset-0 islamic-pattern-overlay opacity-60" />
        <div className="container relative z-10 py-16 md:py-20 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{copy.cta.title}</h2>
          <p className="text-emerald-100/80 max-w-xl mx-auto mb-8 leading-relaxed">{copy.cta.subtitle}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-500/90 text-amber-950 font-semibold rounded-xl px-7 h-12 text-base shadow-glow-gold">
              <Link to={config.primaryHref} className="flex items-center gap-2">
                {copy.cta.primary}
                <ArrowRight className="h-4 w-4 rtl-mirror" aria-hidden="true" />
              </Link>
            </Button>
            <Button asChild size="lg" className="bg-transparent border border-white/30 text-white hover:bg-white/10 rounded-xl px-7 h-12 text-base">
              <Link to="/teachers">{copy.cta.secondary}</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default SpecialtyLanding;
